// apps/web/src/features/crm/leads/activity/components/LeadTaskRow.tsx
//
// Single open-task row in the lead drawer (#1804). Reschedule + complete
// affordances are shown ONLY for the actor's OWN open MANUAL row; auto
// STAGE_PRIMARY / CALLBACK_URGENT rows AND other managers' MANUAL rows are
// read-only here. The list returns every visible open task (incl. peers'
// MANUAL rows), but the backend rejects non-owned edits (409) and #1804
// scopes actions to the actor's own tasks, so we never offer the action on
// a row the actor does not own. The kind hint on a non-MANUAL row tells the
// manager why it cannot be edited here.
import { useTranslation } from 'react-i18next';
import { LeadTaskKind, type LeadTask } from '@magimp/shared';
import { useCancelLeadTask, useCompleteLeadTask } from '@magimp/shared-hooks';
import { Button } from '../../../../../ui/index.js';
import { formatDateTime } from '../../../../../i18n/index.js';
import { useToastStore } from '../../../../../stores/toast.js';
import { errorMessage } from '../../../../../api/errors.js';

export interface LeadTaskRowProps {
  task: LeadTask;
  canManage: boolean;
  /** #2317: admin-or-assigned card gate that unlocks the CANCEL control. */
  canEditCard: boolean;
  /** #2317: admin scope (`all`) may cancel ANY visible MANUAL task, not only own. */
  isAdmin: boolean;
  currentUserId: string | null;
  onReschedule: (task: LeadTask) => void;
}

export function LeadTaskRow({
  task,
  canManage,
  canEditCard,
  isAdmin,
  currentUserId,
  onReschedule,
}: LeadTaskRowProps) {
  const { t } = useTranslation(['leads', 'common']);
  const show = useToastStore((s) => s.show);
  const complete = useCompleteLeadTask(task.id);
  const cancel = useCancelLeadTask(task.id, task.leadId);

  const isManual = task.kind === LeadTaskKind.MANUAL;
  const editable = isManual && canManage && task.assigneeId === currentUserId;
  // #2317: cancel is a SEPARATE, wider gate — an admin may cancel any visible
  // MANUAL task (matches the backend `setCancelled` assignee bypass), a
  // non-admin manager only their own. Reschedule/complete stay own-only.
  const canCancel =
    isManual && canManage && canEditCard && (isAdmin || task.assigneeId === currentUserId);

  const onComplete = () => {
    complete.mutate(undefined, {
      onSuccess: () => show({ tone: 'success', text: t('leads:tasks.toasts.completed') }),
      onError: (err) => show({ tone: 'error', text: errorMessage(err) }),
    });
  };

  const onCancel = () => {
    cancel.mutate(undefined, {
      onError: (err) => show({ tone: 'error', text: errorMessage(err) }),
    });
  };

  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        gap: 4,
        padding: 10,
        borderRadius: 'var(--r-2)',
        border: '1px solid var(--border-1)',
        background: 'var(--bg-1)',
      }}
      data-testid="lead-task-row"
      data-task-id={task.id}
      data-task-kind={task.kind}
    >
      <div
        style={{ display: 'flex', alignItems: 'baseline', gap: 6, justifyContent: 'space-between' }}
      >
        <strong style={{ fontSize: 13, wordBreak: 'break-word' }}>{task.title}</strong>
        {task.dueAt ? (
          <time
            data-testid="lead-task-due"
            dateTime={task.dueAt}
            style={{ fontSize: 11, color: 'var(--fg-3)' }}
          >
            {formatDateTime(task.dueAt)}
          </time>
        ) : (
          <span style={{ fontSize: 11, color: 'var(--fg-3)' }}>{t('leads:tasks.noDue')}</span>
        )}
      </div>
      {!isManual && (
        <div style={{ fontSize: 11, color: 'var(--fg-3)' }} data-testid="lead-task-kind-hint">
          {t('leads:tasks.autoHint')}
        </div>
      )}
      {(editable || canCancel) && (
        <div style={{ display: 'flex', gap: 6, alignItems: 'center', flexWrap: 'wrap' }}>
          <div style={{ flex: 1 }} />
          {/* #2317 D-3: soft-cancel. Editor-only; an admin sees it on any
              MANUAL row (incl. a peer's), a manager only on their own.
              Visually distinct (danger ghost) from the primary Complete so the
              destructive edge reads. */}
          {canCancel && (
            <Button
              size="sm"
              kind="ghost"
              icon="x"
              onClick={onCancel}
              disabled={cancel.isPending}
              data-testid="lead-task-cancel"
              style={{ color: 'var(--status-error-fg)' }}
            >
              {t('leads:tasks.actions.cancel')}
            </Button>
          )}
          {editable && (
            <>
              <Button
                size="sm"
                kind="ghost"
                onClick={() => onReschedule(task)}
                data-testid="lead-task-reschedule"
              >
                {t('leads:tasks.actions.reschedule')}
              </Button>
              <Button
                size="sm"
                kind="primary"
                onClick={onComplete}
                disabled={complete.isPending}
                data-testid="lead-task-complete"
              >
                {t('leads:tasks.actions.complete')}
              </Button>
            </>
          )}
        </div>
      )}
    </div>
  );
}
